// Map of view types to user-friendly names for auto images
const autoViewTypeMap = {
  driversSideFrontView: "Driver's side front",
  passengersSideRearView: "Passenger's side rear"
};

// Map of section keys to user-friendly names for home images
const homeSectionMap = {
  kitchens: 'Kitchen',
  bathrooms: 'Bathroom',
  fireplaces: 'Fireplace',
  pets: 'Pet',
  frontDoors: 'Front door',
  homeExteriors: 'Home exterior',
  electricalBoxes: 'Electrical box',
  waterline: 'Main water supply line',
  swimmingPools: 'Swimming pool',
  trampolines: 'Trampoline'
};

// Map of section keys to user-friendly names for umbrella images
const umbrellaViewTypeMap = {
  // Large watercraft views
  portView: 'Left side (port)',
  starboardView: 'Right side (starboard)',
  serialNumberView: 'Serial number',
  motorView: 'Motor',
  // ATV and personal watercraft views
  leftView: 'Left side',
  rightView: 'Right side',
  motor: 'Motor'
};

// Map of umbrella section keys to user-friendly names
const umbrellaSectionMap = {
  largeWatercraft: 'Large Watercraft',
  personalWatercraft: 'Personal Watercraft',
  atv: 'ATV',
  motorcycle: 'Motorcycle',
  recreationalVehicle: 'Recreational Vehicle'
};

// Add SPP view type map
const sppViewTypeMap = {
  appraisalView: 'Receipt or appraisal',
  propertyView: 'Full image of item'
};

const sortItemsByCategory = (items, category) => {
  if (category === 'homeExteriors') {
    // Home exteriors sorting to order corners from first to fourth
    return [...items].sort((a, b) => {
      if (a.itemId !== b.itemId) {
        if (!a.itemId) {
          return 1;
        }
        if (!b.itemId) {
          return -1;
        }
        return a.itemId.localeCompare(b.itemId);
      }

      return a.imageIndex - b.imageIndex;
    });
  }

  return [...items].sort((a, b) => {
    // First sort by order number - extract the number at the end of the primaryLabel
    const aOrderMatch = a.primaryLabel.match(/\d+$/);
    const bOrderMatch = b.primaryLabel.match(/\d+$/);

    const aOrderNumber = aOrderMatch ? parseInt(aOrderMatch[0]) : 0;
    const bOrderNumber = bOrderMatch ? parseInt(bOrderMatch[0]) : 0;

    if (aOrderNumber !== bOrderNumber) {
      return aOrderNumber - bOrderNumber;
    }

    if (
      [
        'kitchens',
        'bathrooms',
        'fireplaces',
        'pets',
        'frontDoors',
        'homeExteriors',
        'electricalBoxes',
        'waterline',
        'swimmingPools',
        'trampolines'
      ].includes(category)
    ) {
      if (['kitchens', 'bathrooms'].includes(category)) {
        if (a.primaryText.includes('Zoomed-out') && !b.primaryText.includes('Zoomed-out')) {
          return -1;
        }

        if (!a.primaryText.includes('Zoomed-out') && b.primaryText.includes('Zoomed-out')) {
          return 1;
        }

        if (a.primaryText.includes('underneath the sink') && !b.primaryText.includes('underneath the sink')) {
          return -1;
        }

        if (!a.primaryText.includes('underneath the sink') && b.primaryText.includes('underneath the sink')) {
          return 1;
        }
      }
    } else if (category === 'auto') {
      // First sort by itemId (VIN)
      if (a.itemId !== b.itemId) {
        if (!a.itemId) {
          return 1;
        }
        if (!b.itemId) {
          return -1;
        }
        return a.itemId.localeCompare(b.itemId);
      }

      // Then sort by view type to ensure driver's side front comes first
      const viewTypeA = a.key.split('__')[3];
      const viewTypeB = b.key.split('__')[3];

      // Define view type order
      const viewOrder = {
        driversSideFrontView: 1,
        passengersSideRearView: 2
      };

      const orderA = viewOrder[viewTypeA] || 99;
      const orderB = viewOrder[viewTypeB] || 99;

      return orderA - orderB;
    } else if (category === 'spp') {
      // Sort by view type
      const viewTypeA = a.key.split('__')[3];
      const viewTypeB = b.key.split('__')[3];

      if (viewTypeA === 'appraisalView') {
        return -1;
      }
      if (viewTypeB === 'appraisalView') {
        return 1;
      }
      if (viewTypeA === 'propertyView') {
        return -1;
      }
      if (viewTypeB === 'propertyView') {
        return 1;
      }

      return 0;
    } else if (
      ['largeWatercraft', 'personalWatercraft', 'atv', 'motorcycle', 'recreationalVehicle'].includes(category)
    ) {
      // Check if these are motor images
      const aIsMotor = a.primaryText && a.primaryText.startsWith('Motor ');
      const bIsMotor = b.primaryText && b.primaryText.startsWith('Motor ');

      if (aIsMotor && bIsMotor) {
        // Extract motor numbers and compare
        const aMotorNum = parseInt(a.primaryText.replace('Motor ', ''));
        const bMotorNum = parseInt(b.primaryText.replace('Motor ', ''));
        return aMotorNum - bMotorNum;
      }

      if (aIsMotor) {
        return 1; // Motors come after regular images
      }

      if (bIsMotor) {
        return -1; // Regular images come before motors
      }

      // Then sort by view type for regular images
      const viewTypeA = a.key.split('__')[4];
      const viewTypeB = b.key.split('__')[4];

      // Define view type order
      const viewOrder = {
        leftView: 1,
        portView: 1, // Port is left side
        rightView: 2,
        starboardView: 2, // Starboard is right side
        serialNumberView: 3,
        motorView: 4,
        frontView: 1, // For motorcycle - front comes first
        rearView: 2 // For motorcycle - rear comes second
      };

      const orderA = viewOrder[viewTypeA] || 99;
      const orderB = viewOrder[viewTypeB] || 99;

      return orderA - orderB;
    }

    return 0;
  });
};

// Helper function to parse inventory image keys and create user-friendly data
const parseInventoryItems = (inventoryItems) => {
  const result = {
    auto: [],
    umbrella: [],
    spp: [],
    // Home sections
    kitchens: [],
    bathrooms: [],
    fireplaces: [],
    pets: [],
    frontDoors: [],
    homeExteriors: [],
    electricalBoxes: [],
    waterline: [],
    swimmingPools: [],
    trampolines: [],
    // Umbrella sections
    largeWatercraft: [],
    personalWatercraft: [],
    atv: [],
    motorcycle: [],
    recreationalVehicle: []
  };

  // Create a separate tracking object for item order numbers
  const itemOrderMaps = {};

  inventoryItems.forEach(({ key, url }) => {
    const parts = key.split('__');
    const category = parts[0];

    if (category === 'auto') {
      // Format: auto__${policyId}__${vin}__${viewType}
      const vin = parts[2];
      const viewType = parts[3];

      result.auto.push({
        primaryLabel: vin,
        primaryText: autoViewTypeMap[viewType] || viewType,
        key: key,
        url: url,
        itemId: vin
      });
    } else if (category === 'home') {
      // Format: home__${policyId}__${sectionKey}__${itemId}__${imageIndex}
      const sectionKey = parts[2];
      const itemId = parts[3];
      const imageIndex = parts[4] ? parseInt(parts[4]) : 0;

      const sectionTitle = homeSectionMap[sectionKey] || sectionKey;

      // Get specific description based on section and image index
      let imageDescription = '';

      switch (sectionKey) {
        case 'bathrooms':
          if (imageIndex === 0) {
            imageDescription = 'Zoomed-out image of the entire bathroom, including countertops';
          } else if (imageIndex === 1) {
            imageDescription = 'Area underneath sink 1, including pipes';
          } else if (imageIndex === 2) {
            imageDescription = 'Area underneath sink 2, including pipes';
          } else {
            imageDescription = `Bathroom image ${imageIndex + 1}`;
          }
          break;

        case 'kitchens':
          if (imageIndex === 0) {
            imageDescription = 'Zoomed-out image of the entire kitchen, including stove';
          } else if (imageIndex === 1) {
            imageDescription = 'Area underneath the sink, including pipes';
          } else {
            imageDescription = `Kitchen image ${imageIndex + 1}`;
          }
          break;

        case 'homeExteriors':
          if (imageIndex >= 0 && imageIndex <= 3) {
            const corners = ['First', 'Second', 'Third', 'Fourth'];
            imageDescription = `${corners[imageIndex]} corner, including the roof and ground`;
          } else {
            imageDescription = `Home exterior image ${imageIndex + 1}`;
          }
          break;

        case 'frontDoors':
          imageDescription = 'Exterior image, including steps (if applicable)';
          break;

        case 'electricalBoxes':
          imageDescription = 'Entire electrical box with door(s) open';
          break;

        case 'fireplaces':
          imageDescription = 'Entire fireplace';
          break;

        case 'pets':
          imageDescription = "Zoomed-out image, capturing pet's entire body";
          break;

        case 'trampolines':
          imageDescription = 'Entire trampoline';
          break;

        case 'swimmingPools':
          imageDescription = 'Swimming pool';
          break;

        case 'waterline':
          imageDescription = 'Main water supply line';
          break;

        default:
          imageDescription = `${sectionTitle} image ${imageIndex + 1}`;
      }

      if (!itemOrderMaps[sectionKey]) {
        itemOrderMaps[sectionKey] = {};
      }

      let itemOrderNumber;
      if (itemOrderMaps[sectionKey][itemId] !== undefined) {
        itemOrderNumber = itemOrderMaps[sectionKey][itemId];
      } else {
        itemOrderNumber = Object.keys(itemOrderMaps[sectionKey]).length + 1;
        itemOrderMaps[sectionKey][itemId] = itemOrderNumber;
      }

      // Add the image to its section
      result[sectionKey].push({
        primaryLabel: `${sectionTitle.toUpperCase()} ${itemOrderNumber}`,
        primaryText: imageDescription,
        key: key,
        url: url,
        imageUrl: url,
        itemId: itemId,
        imageIndex: imageIndex
      });
    } else if (category === 'umbrella') {
      // Format: umbrella__${policyId}__${sectionKey}__${itemId}__${viewType}
      // For motors: umbrella__${policyId}__${sectionKey}__${itemId}__motor__${motorId}__${orderNumber}
      const sectionKey = parts[2]; // largeWatercraft, personalWatercraft, atv, motorcycle, recreationalVehicle
      const itemId = parts[3];

      const sectionTitle = umbrellaSectionMap[sectionKey] || sectionKey;

      // Check if this is a motor image
      if (parts.length >= 7 && parts[4] === 'motor') {
        const orderNumber = parseInt(parts[6]) + 1; // Convert from 0-based to 1-based

        if (!itemOrderMaps[sectionKey]) {
          itemOrderMaps[sectionKey] = {};
        }

        let itemOrderNumber;
        if (itemOrderMaps[sectionKey][itemId] !== undefined) {
          itemOrderNumber = itemOrderMaps[sectionKey][itemId];
        } else {
          itemOrderNumber = Object.keys(itemOrderMaps[sectionKey]).length + 1;
          itemOrderMaps[sectionKey][itemId] = itemOrderNumber;
        }

        result[sectionKey].push({
          primaryLabel: `${sectionTitle.toUpperCase()} ${itemOrderNumber}`,
          primaryText: `Motor ${orderNumber}`,
          key: key,
          url: url,
          imageUrl: url,
          itemId: itemId
        });
      } else {
        // Regular umbrella image (not a motor)
        const viewType = parts[4];

        const imageDescription = umbrellaViewTypeMap[viewType] || viewType;

        if (!itemOrderMaps[sectionKey]) {
          itemOrderMaps[sectionKey] = {};
        }

        let itemOrderNumber;
        if (itemOrderMaps[sectionKey][itemId] !== undefined) {
          itemOrderNumber = itemOrderMaps[sectionKey][itemId];
        } else {
          itemOrderNumber = Object.keys(itemOrderMaps[sectionKey]).length + 1;
          itemOrderMaps[sectionKey][itemId] = itemOrderNumber;
        }

        result[sectionKey].push({
          primaryLabel: `${sectionTitle.toUpperCase()} ${itemOrderNumber}`,
          primaryText: imageDescription,
          key: key,
          url: url,
          imageUrl: url,
          itemId: itemId
        });
      }
    } else if (category === 'spp') {
      // Format: spp__${policyId}__${itemId}__${viewType}
      const policyId = parts[1];
      const itemId = parts[2];
      const viewType = parts[3];

      const sectionKey = 'spp';
      const sectionTitle = 'SPP Item';

      const imageDescription = sppViewTypeMap[viewType] || viewType;

      if (!result[sectionKey]) {
        result[sectionKey] = [];
      }

      if (!itemOrderMaps[sectionKey]) {
        itemOrderMaps[sectionKey] = {};
      }

      let itemOrderNumber;
      if (itemOrderMaps[sectionKey][itemId] !== undefined) {
        itemOrderNumber = itemOrderMaps[sectionKey][itemId];
      } else {
        itemOrderNumber = Object.keys(itemOrderMaps[sectionKey]).length + 1;
        itemOrderMaps[sectionKey][itemId] = itemOrderNumber;
      }

      result[sectionKey].push({
        primaryLabel: `${sectionTitle} ${itemOrderNumber}`,
        primaryText: imageDescription,
        secondaryLabel: 'POLICY ID',
        secondaryText: policyId,
        key: key,
        url: url,
        imageUrl: url,
        itemId: itemId
      });
    }
  });

  // After all items have been processed, sort each category by itemId and count unique items
  const categoryItemCount = {};

  Object.keys(result).forEach((category) => {
    if (Array.isArray(result[category]) && result[category].length > 0) {
      // Sort items using the dedicated sorting function
      result[category] = sortItemsByCategory(result[category], category);

      // Count unique items
      const hasItemIds = result[category].some((item) => item.itemId);
      let itemCount = result[category].length;

      if (hasItemIds) {
        const uniqueItemIds = new Set();
        result[category].forEach((item) => {
          if (item.itemId) {
            uniqueItemIds.add(item.itemId);
          }
        });

        if (uniqueItemIds.size > 0) {
          itemCount = uniqueItemIds.size;
        }
      }

      categoryItemCount[category] = itemCount;
    } else {
      categoryItemCount[category] = 0;
    }
  });

  // Add image availability flags to decide whether to show the tab in the modal or not
  const hasAutoImages = result.auto.length > 0;

  // Check for any home-related categories
  const homeCategories = [
    'homeExteriors',
    'frontDoors',
    'kitchens',
    'bathrooms',
    'fireplaces',
    'electricalBoxes',
    'waterline',
    'pets',
    'swimmingPools',
    'trampolines'
  ];
  const hasHomeImages = homeCategories.some((category) => result[category].length > 0);

  // Check for any umbrella-related categories
  const umbrellaCategories = ['largeWatercraft', 'personalWatercraft', 'atv', 'motorcycle', 'recreationalVehicle'];
  const hasUmbrellaImages = umbrellaCategories.some((category) => result[category].length > 0);

  // Check for SPP images
  const hasSppImages = result.spp.length > 0;

  return {
    parsedItems: result,
    categoryItemCount,
    hasAutoImages,
    hasHomeImages,
    hasUmbrellaImages,
    hasSppImages,
    homeCategories: homeCategories.filter((category) => result[category].length > 0),
    umbrellaCategories: umbrellaCategories.filter((category) => result[category].length > 0)
  };
};

export default parseInventoryItems;
